/**
 * Loading a baked model (`data/baked/models/*.json`, served at `/baked/…`)
 * and turning one of its LODs into a three.js `BufferGeometry`. Shared by
 * the `/assets` model viewer and the `/fly` flight scene — the conversion
 * itself is the pure, GPU-free `model-geometry.ts`; this module is only the
 * fetch plus the three.js attribute plumbing around it.
 */

import * as THREE from "three/webgpu";
import { buildGeometryAttributes, type BakedModelLodMesh } from "./model-geometry.ts";
import { SHADE_ANCHOR_ATTRIBUTE, SHADE_HALF_WIDTH_ATTRIBUTE } from "./ramp-material.ts";
import { hasShadeableFaces, shadeRampAttributes } from "./ramp-shade.ts";

/** A baked LOD's 2-point faces (`packages/baker`'s `ModelLodLines`): roads, rivers, field boundaries, rigging. Optional here because it arrives with a rebake — a stale `data/baked` should degrade to triangles only. */
export interface BakedModelLodLines {
  readonly positions: readonly number[];
  /** Endpoint indices into `positions`, 2 per segment. */
  readonly indices: readonly number[];
  /** One per segment. `anchorIndex` is the unshaded `BARON.PAL` index — what tells a river from a road (`map/map-features.ts`); optional for the same staleness reason `lines` itself is. */
  readonly colors: readonly { readonly colorRgba: readonly number[]; readonly anchorIndex?: number }[];
}

/**
 * A baked LOD's screen-space sprite (`packages/baker`'s `ModelBillboard`) —
 * `FLAK`/`EXPLO`/`SMOKE`/`MISC` are drawn from nothing else. Optional for
 * the same reason `lines` is: a `data/baked` from before the sprite-node
 * decode has neither field.
 */
export interface BakedModelBillboard {
  readonly position: readonly [number, number, number];
  readonly offsetX: number;
  readonly offsetY: number;
  readonly frameIndex: number;
  readonly frame?: { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
  readonly stateIndex: number;
}

/** A baked LOD's shaded sphere (`packages/baker`'s `ModelSphere`) — pilot heads, tree canopies. */
export interface BakedModelSphere {
  readonly position: readonly [number, number, number];
  readonly radius: number;
  readonly colorRgba: readonly number[];
  readonly stateIndex: number;
}

export interface BakedModelLodBody extends BakedModelLodMesh {
  readonly index: number;
  readonly triangleCount: number;
  /** §3's apparent-size switching threshold; with `boundingRadius` it sets the sprite pixel-to-metre scale. */
  readonly detail?: number;
  readonly lines?: BakedModelLodLines;
  readonly billboards?: readonly BakedModelBillboard[];
  readonly spheres?: readonly BakedModelSphere[];
}

/** The sheet a model's billboard `frameIndex` values address (`packages/baker`'s `ModelSpriteSheet`). */
export interface BakedModelSpriteSheet {
  readonly sheet: string;
  readonly path: string;
  readonly sheetWidth: number;
  readonly sheetHeight: number;
}

export interface BakedModelBody {
  readonly boundingRadius: number;
  /**
   * The low byte of `.TBL` `classFlags` this model baked at — `1 unit = 2^s/20 ft`
   * (gi-format.md §6.1). Already applied to `positions` by `packages/baker`, so
   * a consumer needs it only to tell object *classes* apart: `s = 7` is the
   * river/road/hill/landcover tier and `s = 10` is the two sea plates
   * (`world/terrain-relief-source.ts` is the one caller that cares).
   */
  readonly scaleExponent?: number;
  readonly lods: readonly BakedModelLodBody[];
  readonly spriteSheet?: BakedModelSpriteSheet;
  /** The paint scheme this entry was baked in (`packages/baker`'s `ModelPaintMeta`); absent on every model the engine never repaints. */
  readonly paint?: { readonly scheme: string; readonly colours: readonly number[] };
}

export async function loadBakedModel(path: string): Promise<BakedModelBody> {
  const response = await fetch(`/baked/${path}`);
  if (!response.ok) {
    throw new Error(`failed to load model ${path}: ${response.status} ${response.statusText}`);
  }
  return (await response.json()) as BakedModelBody;
}

/**
 * The highest-numbered LOD that actually has triangles. `defaultLodIndex`
 * (lod-select.ts) answers "which LOD should the viewer's dropdown start
 * on"; this answers "which LOD should something that isn't offering a
 * choice draw", and skips the empty impostor blocks some objects carry
 * (docs/formats/models-tbl.md "Notes for the port": LOD 0/1 are
 * line-and-plate impostors, and several bake outputs have 0 triangles).
 */
export function bestRenderableLod(lods: readonly BakedModelLodBody[]): BakedModelLodBody | undefined {
  return [...lods].sort((a, b) => a.index - b.index).findLast((lod) => lod.triangleCount > 0);
}

/**
 * `bestRenderableLod`, widened to count **every** kind of primitive a LOD can
 * carry — 2-point line faces, sprite billboards and shaded spheres as well as
 * triangles. That is the choice a *scenery* consumer needs, because a `.TBL`
 * LOD is a complete alternative representation of the object and 55 of the
 * corpus's 360 objects draw entirely without triangles
 * (docs/formats/models-tbl.md §1: 48 are nothing but 2-point faces, 7 nothing
 * but sprites).
 *
 * Two things go wrong with the triangle-only rule here. A placed object with
 * no triangle at *any* LOD (`TERR4-95`, in Paris) resolves to `undefined` and
 * is dropped as a load failure, drawing nothing at all. And seven objects
 * across the five regions — `TERR5-11` among them — carry their lines on a
 * *less* detailed LOD than their best triangle one, so those lines were being
 * read off a LOD the geometry didn't come from. One LOD supplies both, which
 * is what "a LOD is one whole representation" means: no cross-LOD mixing, and
 * no object silently reduced to nothing.
 *
 * The sprite/sphere half of the test changes nothing for the shipped regions —
 * the one sphere-bearing object each of them places (the tree,
 * `three/sphere-instances.ts`) carries six trunk triangles on the same LOD, so
 * the triangle test already picked it. It exists so that the rule is "a LOD
 * with any content", stated once, rather than a list of the primitive kinds
 * that happened to matter when it was written.
 */
export function bestDrawableLod(lods: readonly BakedModelLodBody[]): BakedModelLodBody | undefined {
  const hasContent = (lod: BakedModelLodBody): boolean =>
    lod.triangleCount > 0 ||
    (lod.lines?.indices.length ?? 0) >= 2 ||
    (lod.spheres?.length ?? 0) > 0 ||
    (lod.billboards?.length ?? 0) > 0;
  return [...lods].sort((a, b) => a.index - b.index).findLast(hasContent);
}

/**
 * Builds the non-indexed, flat-shaded geometry `model-geometry.ts` describes,
 * plus the two `ramp-shade.ts` attributes a re-shading material walks. The
 * extra pair is 8 bytes a vertex on a 164-triangle aeroplane and it is written
 * unconditionally: a mesh with nothing shadeable simply carries zero
 * half-widths, so the *same* geometry works under either material and no call
 * site has to know which one it will be given.
 */
export function createLodGeometry(lod: BakedModelLodMesh): THREE.BufferGeometry {
  const attributes = buildGeometryAttributes(lod);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(attributes.positions, 3));
  geometry.setAttribute("normal", new THREE.BufferAttribute(attributes.normals, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(attributes.colors, 3));
  const ramp = shadeRampAttributes(lod.faceColors);
  geometry.setAttribute(SHADE_ANCHOR_ATTRIBUTE, new THREE.BufferAttribute(ramp.anchor, 1));
  geometry.setAttribute(SHADE_HALF_WIDTH_ATTRIBUTE, new THREE.BufferAttribute(ramp.halfWidth, 1));
  return geometry;
}

/** Whether this LOD has any face the engine actually re-shades — what decides if a mesh needs the ramp material at all. */
export function lodIsShadeable(lod: BakedModelLodMesh): boolean {
  return hasShadeableFaces(lod.faceColors);
}
